import { useEffect, useRef } from 'react'
import { cn } from '#app/utils/misc'

type Palette = { accent: string; accent2: string; gained: string; lost: string }

const PALETTE: Palette = {
	accent: '#5B4BFF',
	accent2: '#7A5CFF',
	gained: '#25D07A',
	lost: '#F0563F',
}

function hexA(hex: string, a: number) {
	const h = hex.replace('#', '')
	const n = parseInt(h, 16)
	return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${a})`
}

function qbez(a: number, b: number, c: number, t: number) {
	const u = 1 - t
	return u * u * a + 2 * u * t * b + t * t * c
}

// Deterministic PRNG so the layout is stable across renders (no Math.random hydration drift).
function rng(seed: number) {
	return () => {
		seed |= 0
		seed = (seed + 0x6d2b79f5) | 0
		let t = Math.imul(seed ^ (seed >>> 15), 1 | seed)
		t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t
		return ((t ^ (t >>> 14)) >>> 0) / 4294967296
	}
}

type Node = {
	angle: number
	r: number
	size: number
	color: string
	phase: number
	dur: number
	_x: number
	_y: number
	_mx: number
	_my: number
}

/**
 * The signature surface: referring domains converging on a single target.
 * Violet = links, green = newly gained, coral = recently lost. Honors reduced motion.
 */
export function NetworkGraph({
	className,
	label = 'competitor.com',
	nodeCount = 18,
	seed = 77,
}: {
	className?: string
	label?: string
	nodeCount?: number
	seed?: number
}) {
	const canvasRef = useRef<HTMLCanvasElement | null>(null)

	useEffect(() => {
		const canvas = canvasRef.current
		if (!canvas) return
		const c = PALETTE
		const rand = rng(seed)
		const nodes: Node[] = []
		for (let i = 0; i < nodeCount; i++) {
			const angle = (i / nodeCount) * Math.PI * 2 + (rand() - 0.5) * 0.3
			const r = 0.5 + rand() * 0.5
			const roll = rand()
			const color = roll < 0.18 ? c.gained : roll < 0.26 ? c.lost : c.accent
			nodes.push({
				angle,
				r,
				size: 3 + rand() * 4,
				color,
				phase: rand(),
				dur: 1600 + rand() * 1600,
				_x: 0,
				_y: 0,
				_mx: 0,
				_my: 0,
			})
		}

		const reduce =
			typeof window !== 'undefined' &&
			window.matchMedia?.('(prefers-reduced-motion: reduce)').matches

		let ctx: CanvasRenderingContext2D | null = null
		let cw = 0
		let ch = 0
		const resize = () => {
			const dpr = Math.min(window.devicePixelRatio || 1, 2)
			const w = canvas.clientWidth
			const h = canvas.clientHeight
			canvas.width = w * dpr
			canvas.height = h * dpr
			ctx = canvas.getContext('2d')
			ctx?.setTransform(dpr, 0, 0, dpr, 0, 0)
			cw = w
			ch = h
		}
		resize()

		const start = performance.now()
		let raf = 0

		const draw = (ts: number) => {
			if (!ctx) return
			const w = cw
			const h = ch
			const cx = w / 2
			const cy = h / 2
			const rx = Math.min(w * 0.42, 300)
			const ry = Math.min(h * 0.38, 130)
			const build = reduce ? 1 : Math.max(0, Math.min(1, (ts - start) / 1300))
			const ease = Math.max(0, 1 - Math.pow(1 - build, 3))
			ctx.clearRect(0, 0, w, h)

			for (const nd of nodes) {
				const nx = cx + Math.cos(nd.angle) * rx * nd.r
				const ny = cy + Math.sin(nd.angle) * ry * nd.r
				nd._x = cx + (nx - cx) * ease
				nd._y = cy + (ny - cy) * ease
				nd._mx = (nd._x + cx) / 2 + Math.cos(nd.angle + Math.PI / 2) * 22
				nd._my = (nd._y + cy) / 2 + Math.sin(nd.angle + Math.PI / 2) * 22
				ctx.beginPath()
				ctx.moveTo(cx, cy)
				ctx.quadraticCurveTo(nd._mx, nd._my, nd._x, nd._y)
				ctx.strokeStyle = hexA(nd.color, 0.16)
				ctx.lineWidth = 1
				ctx.stroke()
			}

			// travelling pulses along each link (skipped when reduced motion)
			if (build >= 1 && !reduce) {
				for (const nd of nodes) {
					const p = (ts / nd.dur + nd.phase) % 1
					const tt = 1 - p
					const px = qbez(nd._x, nd._mx, cx, tt)
					const py = qbez(nd._y, nd._my, cy, tt)
					ctx.beginPath()
					ctx.arc(px, py, 2, 0, 7)
					ctx.fillStyle = hexA(nd.color, 0.85 * (1 - Math.abs(0.5 - p) * 0.7))
					ctx.fill()
				}
			}

			for (const nd of nodes) {
				const s = nd.size * ease
				ctx.beginPath()
				ctx.arc(nd._x, nd._y, s + 2.5, 0, 7)
				ctx.fillStyle = hexA(nd.color, 0.14)
				ctx.fill()
				ctx.beginPath()
				ctx.arc(nd._x, nd._y, s, 0, 7)
				ctx.fillStyle = nd.color
				ctx.fill()
			}

			const pulse = reduce ? 0 : Math.sin(ts / 700) * 0.5 + 0.5
			ctx.beginPath()
			ctx.arc(cx, cy, 16 + pulse * 9, 0, 7)
			ctx.strokeStyle = hexA(c.accent, 0.18 * (1 - pulse))
			ctx.lineWidth = 1.5
			ctx.stroke()
			ctx.beginPath()
			ctx.arc(cx, cy, 13, 0, 7)
			ctx.fillStyle = c.accent
			ctx.fill()

			ctx.fillStyle = '#151519'
			ctx.font = "600 12px 'IBM Plex Mono', monospace"
			ctx.textAlign = 'center'
			ctx.textBaseline = 'middle'
			ctx.fillText(label, cx, cy + 30)

			if (!reduce || build < 1) raf = requestAnimationFrame(draw)
		}
		raf = requestAnimationFrame(draw)

		// After a resize the canvas backing store is cleared. While animating, the
		// loop repaints on its own; under reduced motion the loop has stopped, so
		// schedule a single redraw to avoid leaving the canvas blank.
		const onResize = () => {
			resize()
			if (reduce) {
				cancelAnimationFrame(raf)
				raf = requestAnimationFrame(draw)
			}
		}
		window.addEventListener('resize', onResize)

		return () => {
			cancelAnimationFrame(raf)
			window.removeEventListener('resize', onResize)
		}
	}, [label, nodeCount, seed])

	return (
		<canvas
			ref={canvasRef}
			className={cn('block h-full w-full', className)}
			role="img"
			aria-label={`Inbound backlink network for ${label}`}
		/>
	)
}
